(() => {
  'use strict';

  const CONFIG = {
    productHandle: 'consultant-reimbursement',
    rate: 0.06625,
    lineProperty: '_pusher_p_consultant_reimbursement',
    label: 'Consultant Reimbursement',
  };

  let variantId = null;
  let syncPromise = null;
  let internalRequest = false;

  const isFeeItem = (item) =>
    item?.handle === CONFIG.productHandle ||
    item?.properties?.[CONFIG.lineProperty] === 'true';

  const isGiftCardItem = (item) =>
    item?.gift_card === true ||
    String(item?.product_type || '').toLowerCase() === 'gift card' ||
    String(item?.handle || '').includes('gift-card');

  const eligibleMerchandiseCents = (cart) =>
    (cart.items || []).reduce((sum, item) => {
      if (isFeeItem(item) || isGiftCardItem(item)) return sum;
      const discountedLineCents = Number(item.final_line_price || 0);
      if (discountedLineCents <= 0) return sum;
      return sum + discountedLineCents;
    }, 0);

  const expectedFeeCents = (cart) =>
    Math.round(eligibleMerchandiseCents(cart) * CONFIG.rate);

  async function getVariantId() {
    if (variantId) return variantId;
    const response = await fetch(`/products/${CONFIG.productHandle}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Unable to load ${CONFIG.label} product.`);
    const product = await response.json();
    const variant = product.variants?.find((entry) => entry.available) || product.variants?.[0];
    if (!variant?.id) throw new Error(`${CONFIG.label} variant was not found.`);
    variantId = Number(variant.id);
    return variantId;
  }

  async function getCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  }

  async function changeLine(key, quantity) {
    internalRequest = true;
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error('Unable to update the reimbursement line.');
      return response.json();
    } finally {
      internalRequest = false;
    }
  }

  async function addFee(quantity) {
    const id = await getVariantId();
    internalRequest = true;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id,
            quantity,
            properties: {
              [CONFIG.lineProperty]: 'true',
              _calculation: '6.625% of discounted paid merchandise; shipping excluded',
            },
          }],
        }),
      });
      if (!response.ok) throw new Error('Unable to add the reimbursement line.');
      return response.json();
    } finally {
      internalRequest = false;
    }
  }

  async function performSync() {
    const cart = await getCart();
    const target = expectedFeeCents(cart);
    const feeLines = (cart.items || []).filter(isFeeItem);
    const primary = feeLines[0];

    for (const duplicate of feeLines.slice(1)) {
      await changeLine(duplicate.key, 0);
    }

    if (target === 0) {
      if (primary) await changeLine(primary.key, 0);
      return { changed: Boolean(primary || feeLines.length > 1), target };
    }

    if (!primary) {
      await addFee(target);
      return { changed: true, target };
    }

    if (Number(primary.quantity) !== target) {
      await changeLine(primary.key, target);
      return { changed: true, target };
    }

    return { changed: feeLines.length > 1, target };
  }

  function syncFee() {
    if (syncPromise) return syncPromise;
    syncPromise = performSync()
      .catch((error) => {
        console.error('[Pusher P reimbursement]', error);
        throw error;
      })
      .finally(() => {
        syncPromise = null;
      });
    return syncPromise;
  }

  function setCheckoutBusy(busy) {
    document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], a[href="/checkout"], [data-checkout-button]'
    ).forEach((element) => {
      if ('disabled' in element) element.disabled = busy;
      element.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
  }

  async function verifiedCheckout(event, target) {
    event.preventDefault();
    event.stopImmediatePropagation();
    setCheckoutBusy(true);
    try {
      await syncFee();
      window.location.assign('/checkout');
    } catch (error) {
      alert('We could not verify the Consultant Reimbursement. Please refresh the cart and try again.');
      setCheckoutBusy(false);
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target.closest(
      'button[name="checkout"], input[name="checkout"], a[href="/checkout"], [data-checkout-button]'
    );
    if (target) verifiedCheckout(event, target);
  }, true);

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (submitter?.name === 'checkout' || event.target?.action?.includes('/checkout')) {
      verifiedCheckout(event, submitter || event.target);
    }
  }, true);

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const url = String(args[0]?.url || args[0] || '');
    if (!internalRequest && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
      window.setTimeout(() => syncFee().catch(() => {}), 150);
    }
    return response;
  };

  document.addEventListener('DOMContentLoaded', () => {
    syncFee().catch(() => {});
  });

  window.PusherPConsultantReimbursement = Object.freeze({ sync: syncFee, config: CONFIG });
})();
